import { LoggedUser } from './LoginModal';
import { CaseReviewModal } from './CaseReviewModal';

export class SupervisorConsoleView {
  private container: HTMLElement;
  private apiBaseUrl: string;
  private currentUser: LoggedUser;
  private activeTab: 'videowall' | 'cases' | 'heatmap' = 'cases';
  private wallRefreshTimer: number | null = null;

  constructor(container: HTMLElement, apiBaseUrl: string, currentUser: LoggedUser) {
    this.container = container;
    this.apiBaseUrl = apiBaseUrl;
    this.currentUser = currentUser;
  }

  public async render(): Promise<void> {
    this.container.innerHTML = `
      <div style="display:flex; flex-direction:column; height:100%; width:100%; background:#0b1120; color:#f8fafc; font-family:var(--font-sans, system-ui, sans-serif); overflow:hidden;">
        
        <!-- Pestañas de Navegación del Supervisor -->
        <div style="display:flex; justify-content:space-between; align-items:center; background:#0f172a; border-bottom:1px solid #1e293b; padding:8px 20px;">
          <div style="display:flex; gap:8px;">
            <button id="tab-sup-cases" class="tab-btn ${this.activeTab === 'cases' ? 'active' : ''}" 
              style="padding:8px 16px; border-radius:8px; font-weight:700; font-size:0.85rem; cursor:pointer; border:1px solid ${this.activeTab === 'cases' ? '#0284c7' : '#334155'}; background:${this.activeTab === 'cases' ? '#0284c7' : 'transparent'}; color:#fff;">
              📁 Expedientes y Auditoría Operativa
            </button>
            <button id="tab-sup-videowall" class="tab-btn ${this.activeTab === 'videowall' ? 'active' : ''}" 
              style="padding:8px 16px; border-radius:8px; font-weight:700; font-size:0.85rem; cursor:pointer; border:1px solid ${this.activeTab === 'videowall' ? '#ef4444' : '#334155'}; background:${this.activeTab === 'videowall' ? '#ef4444' : 'transparent'}; color:#fff; display:inline-flex; align-items:center; gap:6px;">
              <span style="width:8px; height:8px; border-radius:50%; background:#fff; animation:pulse-dot 1.5s infinite;"></span>
              📺 Video Wall en Vivo (4 Canales)
            </button>
            <button id="tab-sup-heatmap" class="tab-btn ${this.activeTab === 'heatmap' ? 'active' : ''}" 
              style="padding:8px 16px; border-radius:8px; font-weight:700; font-size:0.85rem; cursor:pointer; border:1px solid ${this.activeTab === 'heatmap' ? '#f59e0b' : '#334155'}; background:${this.activeTab === 'heatmap' ? '#f59e0b' : 'transparent'}; color:#fff; display:inline-flex; align-items:center; gap:6px;">
              🔥 Mapa de Calor Geográfico
            </button>
          </div>

          <div style="display:flex; align-items:center; gap:12px;">
            <div style="font-size:0.8rem; color:#94a3b8;">
              Agencia: <strong style="color:#38bdf8;">${this.currentUser.agencyName || 'Todas las Agencias'}</strong>
            </div>
            ${this.currentUser.role === 'ADMIN' ? `
              <button id="btn-purge-calls" style="background:rgba(239,68,68,0.15); border:1px solid #ef4444; color:#fca5a5; padding:4px 10px; border-radius:6px; font-size:0.75rem; font-weight:700; cursor:pointer;" title="Eliminar todas las llamadas, videos y rastros de prueba">
                🗑️ Vaciar Historial
              </button>
            ` : ''}
          </div>
        </div>

        <!-- Área de Contenido Principal -->
        <div id="sup-tab-content-area" style="flex:1; min-height:0; overflow:hidden; position:relative;">
        </div>
      </div>
    `;

    this.setupTabListeners();
    this.renderActiveTabContent();
  }

  private setupTabListeners(): void {
    this.container.querySelector('#tab-sup-cases')?.addEventListener('click', () => {
      this.activeTab = 'cases';
      this.clearWallTimer();
      this.render();
    });

    this.container.querySelector('#tab-sup-videowall')?.addEventListener('click', () => {
      this.activeTab = 'videowall';
      this.render();
    });

    this.container.querySelector('#tab-sup-heatmap')?.addEventListener('click', () => {
      this.activeTab = 'heatmap';
      this.clearWallTimer();
      this.render();
    });

    this.container.querySelector('#btn-purge-calls')?.addEventListener('click', async () => {
      if (!confirm('⚠️ ¿Está completamente seguro de vaciar el historial de llamadas, grabaciones, fotos y GPS? Esta acción no se puede deshacer.')) return;
      try {
        const res = await fetch(`${this.apiBaseUrl}/api/v1/media/cases/purge-all`, { method: 'POST' });
        const json = await res.json();
        alert(json.message || 'Historial vaciado.');
        this.render();
      } catch (e) {
        alert('Error vaciando historial.');
      }
    });
  }

  private clearWallTimer(): void {
    if (this.wallRefreshTimer) {
      clearInterval(this.wallRefreshTimer);
      this.wallRefreshTimer = null;
    }
  }

  private async renderActiveTabContent(): Promise<void> {
    const area = this.container.querySelector('#sup-tab-content-area') as HTMLElement;
    if (!area) return;
    area.innerHTML = '';

    if (this.activeTab === 'cases') {
      const caseReview = new CaseReviewModal(
        this.apiBaseUrl,
        this.currentUser.role,
        this.currentUser.agencyId,
        () => {}
      );
      await caseReview.mount(area);

      const backdrop = area.querySelector('.modal-backdrop') as HTMLElement;
      if (backdrop) {
        backdrop.style.position = 'static';
        backdrop.style.inset = 'auto';
        backdrop.style.background = 'transparent';
        backdrop.style.padding = '0';
        backdrop.style.height = '100%';
        backdrop.style.width = '100%';
      }

      const card = area.querySelector('.modal-card') as HTMLElement;
      if (card) {
        card.style.maxWidth = '100%';
        card.style.width = '100%';
        card.style.height = '100%';
        card.style.maxHeight = '100%';
        card.style.borderRadius = '0';
        card.style.border = 'none';
        card.style.background = '#0b1120';
      }

      const closeBtn = area.querySelector('#btn-close-cases-modal') as HTMLElement;
      if (closeBtn) closeBtn.style.display = 'none';
    } else if (this.activeTab === 'videowall') {
      this.renderVideoWall(area);
    } else if (this.activeTab === 'heatmap') {
      this.renderHeatMap(area);
    }
  }

  private maximizedSlotIndex: number | null = null;
  private lastActiveCallsSnapshot: string = '';

  // --- MODO VIDEO WALL (Mural 2x2 hasta 4 llamadas activas) ---
  private async renderVideoWall(area: HTMLElement): Promise<void> {
    const isMaximized = this.maximizedSlotIndex !== null;
    area.innerHTML = `
      <div style="height:100%; display:flex; flex-direction:column; background:#070b14; padding:16px; box-sizing:border-box;">
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;">
          <div>
            <h2 style="margin:0; font-size:1.15rem; color:#fff; display:flex; align-items:center; gap:8px;">
              <span style="color:#ef4444;">●</span> Mural de Video Wall Operativo en Tiempo Real
              ${isMaximized ? `<span style="background:rgba(2,132,199,0.2); border:1px solid #0284c7; color:#38bdf8; font-size:0.75rem; padding:2px 8px; border-radius:6px; font-weight:700;">Canal ${(this.maximizedSlotIndex ?? 0) + 1} Maximizado</span>` : ''}
            </h2>
            <p style="margin:2px 0 0; font-size:0.78rem; color:#94a3b8;">
              ${isMaximized ? 'Visualización enfocada a pantalla completa. Presione "Restaurar" o la tecla Esc para volver a la cuadrícula 2x2.' : 'Supervisión simultánea de hasta 4 llamadas activas de la corporación.'}
            </p>
          </div>
          <div style="display:flex; align-items:center; gap:8px;">
            ${isMaximized ? `
              <button id="btn-restore-grid-top" style="padding:6px 14px; font-size:0.8rem; background:#0284c7; border:none; border-radius:6px; cursor:pointer; font-weight:700; color:#fff; display:inline-flex; align-items:center; gap:6px;">
                🗗 Restaurar Cuadrícula (2x2)
              </button>
            ` : ''}
            <button id="btn-refresh-wall" class="btn-ghost" style="padding:6px 12px; font-size:0.8rem; border:1px solid #334155; color:#38bdf8;">
              🔄 Refrescar Canales
            </button>
          </div>
        </div>

        <div id="video-wall-grid" style="flex:1; display:grid; ${isMaximized ? 'grid-template-columns:1fr; grid-template-rows:1fr;' : 'grid-template-columns:1fr 1fr; grid-template-rows:1fr 1fr;'} gap:12px; min-height:0; position:relative;">
          <div style="grid-column:1/-1; display:flex; align-items:center; justify-content:center; color:#64748b; font-size:0.9rem;">
            Cargando canales activos...
          </div>
        </div>
      </div>
    `;

    area.querySelector('#btn-restore-grid-top')?.addEventListener('click', () => {
      this.maximizedSlotIndex = null;
      this.lastActiveCallsSnapshot = '';
      this.renderVideoWall(area);
    });

    area.querySelector('#btn-refresh-wall')?.addEventListener('click', () => {
      this.lastActiveCallsSnapshot = '';
      this.updateWallGrid(area);
    });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.maximizedSlotIndex !== null) {
        this.maximizedSlotIndex = null;
        this.lastActiveCallsSnapshot = '';
        this.renderVideoWall(area);
      }
    };
    window.addEventListener('keydown', onKey, { once: true });

    await this.updateWallGrid(area);

    this.clearWallTimer();
    this.wallRefreshTimer = window.setInterval(() => {
      this.updateWallGrid(area);
    }, 8000);
  }

  private async updateWallGrid(area: HTMLElement): Promise<void> {
    const grid = area.querySelector('#video-wall-grid');
    if (!grid) return;

    try {
      const res = await fetch(`${this.apiBaseUrl}/api/v1/media/active-calls?agencyId=${encodeURIComponent(this.currentUser.agencyId || '')}`);
      const json = await res.json();
      const calls: any[] = json.data || [];

      // Si las llamadas y el modo de maximizado no han cambiado, mantener iframes para no reiniciar streams
      const snapshotKey = `${JSON.stringify(calls.map(c => c.sessionId))}_${this.maximizedSlotIndex}`;
      if (this.lastActiveCallsSnapshot === snapshotKey) {
        return;
      }
      this.lastActiveCallsSnapshot = snapshotKey;

      if (calls.length === 0) {
        this.maximizedSlotIndex = null;
        grid.innerHTML = `
          <div style="grid-column:1/-1; grid-row:1/-1; display:flex; flex-direction:column; align-items:center; justify-content:center; background:#0b1120; border:1px dashed #334155; border-radius:12px; color:#64748b;">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="margin-bottom:12px;"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect><line x1="8" y1="21" x2="16" y2="21"></line><line x1="12" y1="17" x2="12" y2="21"></line></svg>
            <div style="font-size:1rem; font-weight:700; color:#cbd5e1;">Sin Llamadas Activas en este Momento</div>
            <div style="font-size:0.8rem; margin-top:4px;">Los canales se activarán automáticamente cuando los ciudadanos enlacen una transmisión 911.</div>
          </div>
        `;
        return;
      }

      // Rellenar cuadrantes (1 cuadrante grande si está maximizado, o cuadrícula 2x2 de 4 cuadrantes)
      const slots = this.maximizedSlotIndex !== null ? [this.maximizedSlotIndex] : [0, 1, 2, 3];
      grid.innerHTML = slots.map(index => {
        const call = calls[index];
        if (!call) {
          return `
            <div style="background:#0b1120; border:1px dashed #1e293b; border-radius:10px; display:flex; align-items:center; justify-content:center; color:#475569; font-size:0.85rem;">
              <span>Canal ${index + 1} • En Espera</span>
            </div>
          `;
        }

        const isMaximized = this.maximizedSlotIndex === index;
        const spectatorUrl = `/ambulance/?sessionId=${call.sessionId}&token=${call.spectatorToken || 'spectator'}`;
        return `
          <div style="background:#000; border:2px solid ${isMaximized ? '#38bdf8' : '#0284c7'}; border-radius:10px; overflow:hidden; position:relative; display:flex; flex-direction:column; width:100%; height:100%;">
            <!-- Header Táctico del Canal -->
            <div style="position:absolute; top:8px; left:8px; z-index:10; background:rgba(0,0,0,0.75); backdrop-filter:blur(6px); padding:4px 10px; border-radius:6px; border:1px solid #38bdf8; font-size:0.75rem; color:#fff; display:flex; align-items:center; gap:6px;">
              <span style="width:7px; height:7px; border-radius:50%; background:#ef4444; animation:pulse-dot 1.5s infinite;"></span>
              <strong>${call.incidentFolio}</strong> • <span>${call.dispatcherName}</span>
            </div>

            <!-- Botones de Control: Maximizar / Popout -->
            <div style="position:absolute; top:8px; right:8px; z-index:10; display:flex; align-items:center; gap:6px;">
              <button class="btn-toggle-max" data-slot="${index}" style="background:${isMaximized ? '#0284c7' : 'rgba(15,23,42,0.85)'}; backdrop-filter:blur(6px); border:1px solid ${isMaximized ? '#38bdf8' : '#475569'}; color:#fff; padding:4px 10px; border-radius:6px; font-size:0.75rem; font-weight:700; cursor:pointer; display:flex; align-items:center; gap:4px;" title="${isMaximized ? 'Restaurar cuadrícula 2x2' : 'Maximizar este canal a pantalla completa'}">
                ${isMaximized ? '🗗 Restaurar (2x2)' : '⛶ Maximizar'}
              </button>
              <a href="${spectatorUrl}" target="_blank" style="background:rgba(15,23,42,0.85); backdrop-filter:blur(6px); border:1px solid #475569; color:#94a3b8; padding:4px 8px; border-radius:6px; font-size:0.75rem; text-decoration:none; display:flex; align-items:center; gap:4px;" title="Abrir en ventana independiente (Monitor de Centro de Comando)">
                ↗
              </a>
            </div>

            <iframe src="${spectatorUrl}" style="width:100%; height:100%; border:none;" allow="camera; microphone; geolocation; autoplay"></iframe>
          </div>
        `;
      }).join('');

      // Asignar listeners de maximizado
      grid.querySelectorAll('.btn-toggle-max').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const slot = parseInt((e.currentTarget as HTMLElement).getAttribute('data-slot') || '0', 10);
          if (this.maximizedSlotIndex === slot) {
            this.maximizedSlotIndex = null;
          } else {
            this.maximizedSlotIndex = slot;
          }
          this.lastActiveCallsSnapshot = '';
          this.renderVideoWall(area);
        });
      });
    } catch (_) {}
  }

  // --- MAPA DE CALOR (HEATMAP GEOGRÁFICO DE INCIDENTES) ---
  private async renderHeatMap(area: HTMLElement): Promise<void> {
    area.innerHTML = `
      <div style="height:100%; display:flex; flex-direction:column; background:#0b1120; padding:16px; box-sizing:border-box;">
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;">
          <div>
            <h2 style="margin:0; font-size:1.15rem; color:#fff; display:flex; align-items:center; gap:8px;">
              🔥 Mapa de Calor e Incidencia Geográfica
            </h2>
            <p style="margin:2px 0 0; font-size:0.78rem; color:#94a3b8;">
              Densidad y concentración de llamadas de emergencia en el territorio.
            </p>
          </div>
          <div style="font-size:0.78rem; color:#64748b;">
            Actualización satelital continua OpenStreetMap
          </div>
        </div>

        <div id="sup-heatmap-canvas" style="flex:1; border-radius:12px; overflow:hidden; border:1px solid #334155; min-height:0; position:relative;">
        </div>
      </div>
    `;

    const mapSlot = area.querySelector('#sup-heatmap-canvas') as HTMLElement;
    if (!mapSlot) return;

    try {
      // Cargar todas las coordenadas registradas de la agencia
      const res = await fetch(`${this.apiBaseUrl}/api/v1/media/cases?agencyId=${encodeURIComponent(this.currentUser.agencyId || '')}`);
      const json = await res.json();
      const cases: any[] = json.data || [];

      // Importar leaflet dinámicamente si no está presente
      const L = (window as any).L;
      if (!L) {
        mapSlot.innerHTML = `<div style="color:#ef4444; padding:20px;">Leaflet map no está cargado.</div>`;
        return;
      }

      // Centro de operaciones por defecto (coordenadas de referencia inicial)
      const map = L.map(mapSlot).setView([21.8853, -102.2916], 13);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);

      // Traer puntos de cada caso
      for (const c of cases) {
        fetch(`${this.apiBaseUrl}/api/v1/media/sessions/${c.sessionId}/forensics`)
          .then(r => r.json())
          .then(data => {
            const tracks = data.data?.gpsTracks || [];
            if (tracks.length > 0) {
              const p = tracks[0];
              // Círculo de calor simulado (Heat point)
              L.circle([p.latitude, p.longitude], {
                color: '#ef4444',
                fillColor: '#f97316',
                fillOpacity: 0.5,
                radius: 120
              }).addTo(map).bindPopup(`
                <strong>${c.incidentFolio}</strong><br/>
                📱 ${c.phoneNumber || 'Sin Tel.'}<br/>
                👨‍⚕️ ${c.dispatcherName}<br/>
                🕒 ${new Date(c.createdAt).toLocaleString()}
              `);
            }
          })
          .catch(() => {});
      }
    } catch (_) {}
  }
}
